import Vue from 'vue';
import './plugins/vuetify';
import router from '@/plugins/router';
import store from '@/plugins/vuex/';
import { createProvider } from '@/plugins/vue-apollo';
import { AUTH_TOKEN, PESO_SYMBOL, FACEBOOK_PIXEL_ID, SEGMENT_WRITE_KEY, GOOGLE_PLATFORM_KEY, LOGIN_TYPE, GOOGLE_TAG_MANAGER_KEY, AES_SECRET_KEY, USERPILOT_APP_TOKEN, } from '@/helpers/constants/app';
import JWT from '@/helpers/entities/classes/auth/Jwt';
import { Userpilot } from 'userpilot';
import BaseText from '@/components/BaseText.vue';
import VueCurrencyFilter from 'vue-currency-filter';
import BaseInnerLoader from '@/components/v2/BaseInnerLoader';
import BaseImageThumbnail from '@/components/v2/BaseImageThumbnail';
import VueGtm from '@gtm-support/vue2-gtm';
import * as firebase from 'firebase/app';
import VueFacebookPixel from 'vue-analytics-facebook-pixel';
import VueSegmentAnalytics from 'vue-segment-analytics';
import * as VueGoogleMaps from 'vue2-google-maps';
import CryptoJS from 'crypto-js';
// import * as Sentry from '@sentry/browser';
// import { Vue as VueIntegration } from '@sentry/integrations';
// import { Integrations } from '@sentry/tracing';
import * as Sentry from '@sentry/vue';
import Bugsnag from '@/plugins/bugsnag';
import { AlertPlugin } from 'bootstrap-vue';
import VueCountryCode from 'vue-country-code';
import VueFbCustomerChat from 'vue-fb-customer-chat';
import i18n from './i18n';
import App from './App.vue';
import '@/assets/stylesheets/utils/global-modifiers.scss';
import 'firebase/firestore';
import 'firebase/analytics';
import 'firebase/performance';
import { standardDate } from './helpers/DateFormatter';
// import { cookie } from './helpers/Cookie';
// Import Bootstrap and BootstrapVue CSS files (order is important)
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
// Initialize Userpilot
Userpilot.initialize(USERPILOT_APP_TOKEN);
// Initialize Bugsnag
const bugsnagVue = Bugsnag.getPlugin('vue');
bugsnagVue.installVueErrorHandler(Vue);
// const env = process.env.NODE_ENV;
// const packageVersion = process.env.PACKAGE_VERSION;
// Temporarily Disable Sentry
//
// Sentry.init({
//   release: `ChatGenie-CMS@${env}@${packageVersion}`,
//   environment: env,
//   dsn: 'https://e138ee2bde84480fa4684184568de539@o479005.ingest.sentry.io/5522812',
//   integrations: [
//     new VueIntegration({
//       Vue,
//       tracing: true,
//       tracingOptions: {
//         trackComponents: true,
//       },
//     }),
//     new Integrations.BrowserTracing(),
//   ],
//   // We recommend adjusting this value in production, or using tracesSampler
//   // for finer control
//   tracesSampleRate: 0.25,
// });
Sentry.init({
    Vue,
    dsn: 'https://1592fcbb98883068b159e903f22e8835@o4508730155466753.ingest.us.sentry.io/4508731440168960',
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['*'],
    environment: process.env.NODE_ENV || 'development',
    enabled: ['staging', 'production'].includes(process.env.NODE_ENV || 'development'),
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    release: '2.42.24',
});
/**
 * Populate Vuex Session Token with token from localStorage
 * upon initializing App.
 * We need to populate token here in main.ts since `Vue Router` initialized
 * before Vue Instance is Mounted to reactively track if user is authenticated.
 * PS: Vue Router and Store are accessible even not included in Vue Instance
 */
// const token = cookie(AUTH_TOKEN);
// const loginType = cookie(LOGIN_TYPE);
const token = sessionStorage.getItem(AUTH_TOKEN);
const loginType = sessionStorage.getItem(LOGIN_TYPE);
// Validate Token on Page Reload and App Initialization
//  This will help us to validate if token is already expired and
//    redirect client into home page to login again.
if (token) {
    const encodedToken = CryptoJS.enc.Base64.parse(token);
    const base64Token = encodedToken.toString(CryptoJS.enc.Utf8).split('\n').join('');
    const received = CryptoJS.enc.Base64.parse(base64Token);
    const salt = CryptoJS.enc.Hex.parse(received.toString().slice(0, 32));
    const iv = CryptoJS.enc.Hex.parse(received.toString().slice(32, 64));
    const cipher = CryptoJS.enc.Hex.parse(received.toString().slice(64));
    const key = CryptoJS.PBKDF2(AES_SECRET_KEY, salt, {
        keySize: 256 / 32,
        iterations: 100,
    });
    const decodedCipher = CryptoJS.AES.decrypt(cipher.toString(CryptoJS.enc.Base64), key, {
        iv,
    }).toString(CryptoJS.enc.Utf8);
    const jwt = new JWT(decodedCipher);
    if (jwt.isValidJwtCreated()) {
        store.commit('session/authenticate', { token, loginType });
        store.commit('navigation_drawer/toggleDrawer', true);
    }
    else {
        store.commit('session/logout');
        store.commit('navigation_drawer/toggleDrawer', false);
    }
}
Vue.config.productionTip = false;
Vue.component('base-text', BaseText);
Vue.component('base-inner-loader', BaseInnerLoader);
Vue.component('base-image-thumbnail', BaseImageThumbnail);
/**
 * Vue Currency Filter
 * @see https://github.com/mazipan/vue-currency-filter
 */
Vue.use(VueCurrencyFilter, {
    symbol: PESO_SYMBOL,
    thousandsSeparator: ',',
    fractionSeparator: '.',
    fractionCount: 2,
});
/**
 * @see https://github.com/dmnCodes/vue-fb-customer-chat
 * whitelist the domain url in facebook business account
 * go to chat -> settings -> chatplugin -> select standard -> add domain
 */
if (process.env.NODE_ENV === 'production') {
    Vue.use(VueFbCustomerChat, {
        page_id: '845307082313829',
        theme_color: '#0a7cff',
        locale: 'en_US',
    });
}
Vue.use(VueGtm, {
    id: GOOGLE_TAG_MANAGER_KEY,
    debug: false,
    vueRouter: router,
});
Vue.use(VueCountryCode);
/**
 * Global Standard Date Filter
 * @author Peter John Alvarado <peterjohn@gorated.ph>
 * @since Version {0.6.0}
 */
Vue.filter('standardDate', (date) => {
    if (!date) {
        return '';
    }
    return standardDate(date);
});
/**
 * Global Standard Truncate Filter
 * @author Johsua Timario <johsua@gorated.ph>
 * @since Version (2.16.0)
 */
Vue.filter('truncate', (text, length, clamp) => {
    clamp = clamp || '...';
    const node = document.createElement('div');
    node.innerHTML = text;
    const content = node.textContent;
    const contentLength = content?.length || 0;
    return contentLength > length ? content?.slice(0, length) + clamp : content;
});
/**
 * Firebase Analytics Integration
 * @author Peter John Alvarado <peterjohn@gorated.ph>
 * @since Version {0.3.0}
 */
if (process.env.NODE_ENV === 'production') {
    const firebaseConfig = {
        apiKey: 'AIzaSyBqurRYcznh5HWDxoyiJSTcJ6Xw93GzGcw',
        authDomain: 'chatgenie-web.firebaseapp.com',
        databaseURL: 'https://chatgenie-web.firebaseio.com',
        projectId: 'chatgenie-web',
        storageBucket: 'chatgenie-web.appspot.com',
        messagingSenderId: '353011123535',
        appId: '1:353011123535:web:9d359a1d6d528eb403313a',
        measurementId: 'G-BTEZWPD967',
    };
    firebase.initializeApp(firebaseConfig);
    firebase.analytics();
    firebase.performance();
    // }
    // firebase alias
    Vue.prototype.$analytics = firebase.analytics();
}
Vue.use(VueFacebookPixel);
/**
 * Facebook Pixel ID Initialization
 * @author Angelo David <adavid@gorated.ph>
 * @since Version {0.25.0}
 *
 * Reference: https://www.npmjs.com/package/vue-analytics-facebook-pixel#facebook-script
 */
Vue.prototype.$analytics.fbq.init(FACEBOOK_PIXEL_ID);
Vue.prototype.$analytics.fbq.event('PageVisit');
Vue.use(VueSegmentAnalytics, {
    id: SEGMENT_WRITE_KEY,
});
/**
 * Vue Google Maps Initialization
 * @author Angelo David <adavid@gorated.ph>
 * @since Version {0.63.3}
 *
 * Reference: https://github.com/xkjyeah/vue-google-maps#quickstart-webpack-nuxt
 */
Vue.use(VueGoogleMaps, {
    load: {
        key: GOOGLE_PLATFORM_KEY,
        libraries: 'places',
        v: 3.38,
    },
    installComponents: true,
});
// Make BootstrapVue available throughout your project
Vue.use(AlertPlugin);
new Vue({
    router,
    watch: {
        $route: {
            handler: () => Userpilot.reload(),
            deep: true,
            immediate: true,
        },
    },
    store,
    apolloProvider: createProvider(),
    i18n,
    render: (h) => h(App),
}).$mount('#app');
